/**
 * Hook to capture microphone audio and expose an AnalyserNode.
 * No per-frame React state updates; the canvas renderer pulls data directly.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export function useAudioVisualizer() {
  const [hasStream, setHasStream] = useState(false)
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)

  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const start = useCallback(async (): Promise<boolean> => {
    // Only guard on the ref (not stale state)
    if (analyserRef.current) return true

    if (!navigator.mediaDevices?.getUserMedia) {
      console.warn('getUserMedia not supported')
      return false
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext
      const audioCtx: AudioContext = new AudioCtx()
      audioCtxRef.current = audioCtx

      if (audioCtx.state === 'suspended') {
        try {
          await audioCtx.resume()
        } catch { /* ignore */ }
      }

      const a = audioCtx.createAnalyser()
      a.fftSize = 1024
      a.smoothingTimeConstant = 0.0

      const src = audioCtx.createMediaStreamSource(stream)
      sourceRef.current = src
      src.connect(a)

      analyserRef.current = a
      setAnalyser(a)
      setHasStream(true)
      return true
    } catch (e) {
      console.error('Failed to get microphone access:', e)
      try {
        streamRef.current?.getTracks()?.forEach(t => t.stop())
      } catch { /* ignore */ }
      streamRef.current = null
      return false
    }
  }, [])

  const stop = useCallback(() => {
    try {
      sourceRef.current?.disconnect()
    } catch { /* ignore */ }
    try {
      analyserRef.current?.disconnect()
    } catch { /* ignore */ }
    try {
      streamRef.current?.getTracks()?.forEach(t => t.stop())
    } catch { /* ignore */ }
    try {
      audioCtxRef.current?.close()
    } catch { /* ignore */ }

    sourceRef.current = null
    analyserRef.current = null
    audioCtxRef.current = null
    streamRef.current = null

    setAnalyser(null)
    setHasStream(false)
  }, [])

  useEffect(() => {
    return () => stop()
  }, [stop])

  return {
    analyser,
    hasStream,
    start,
    stop,
  }
}
